import React, {createContext, useContext, useEffect, useState} from 'react'
import {ChartColors} from '../../common/constants/chartColors'
import {ProjectGuestPortalClient} from '../../infrastracture/api/ProjectGuestPortalClient'

export interface GuestTheme {
  onSite: string
  home: string
  notWorking: string
  noResponse: string
}

// Same four day-state colours the Teams client reads out of theme_OnSite/_Home/_NotWorking/
// _NoResponse, with the same ChartColors fallbacks, so the portal and Teams agree even when
// the account has never set a theme.
export const DEFAULT_GUEST_THEME: GuestTheme = {
  onSite: ChartColors.OnSite,
  home: ChartColors.Home,
  notWorking: ChartColors.NotWorking,
  noResponse: ChartColors.NoResponse,
}

interface ProjectGuestContextValue {
  projects: any[]
  selectedProjectId: string | undefined
  selectedProject: any | undefined
  setSelectedProjectId: (id: string) => void
  loading: boolean
  reload: () => void
  profile: any | undefined
  theme: GuestTheme
  assetTypeName: string | undefined
}

// Multi-project guests: v1 renders the first project (StartDate/Created order, as /projects/me
// already returns) and shows a switcher only when there's more than one — Phase 4.4.
const ProjectGuestContext = createContext<ProjectGuestContextValue>({
  projects: [],
  selectedProjectId: undefined,
  selectedProject: undefined,
  setSelectedProjectId: () => {},
  loading: true,
  reload: () => {},
  profile: undefined,
  theme: DEFAULT_GUEST_THEME,
  assetTypeName: undefined,
})

export function ProjectGuestProvider({children}: {children: React.ReactNode}) {
  const [projects, setProjects] = useState<any[]>([])
  const [selectedProjectId, setSelectedProjectId] = useState<string | undefined>(undefined)
  const [loading, setLoading] = useState(true)
  const [profile, setProfile] = useState<any | undefined>(undefined)
  const [theme, setTheme] = useState<GuestTheme>(DEFAULT_GUEST_THEME)
  const [assetTypeName, setAssetTypeName] = useState<string | undefined>(undefined)

  const load = async () => {
    setLoading(true)
    const response = await ProjectGuestPortalClient.GetMyProjects()
    if (response.successful) {
      const data = response.data || []
      setProjects(data)
      setSelectedProjectId((current: string | undefined) =>
        current && data.some((p: any) => p.id === current) ? current : data[0]?.id
      )
    }
    setLoading(false)
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Who is signed in — the header shows their name. Failing this must not block the portal,
  // so there is no loading gate on it; the header simply stays empty until it lands.
  useEffect(() => {
    let cancelled = false

    ProjectGuestPortalClient.GetMyProfile().then((response) => {
      if (cancelled || !response.successful) return
      setProfile(response.data)
    })

    return () => {
      cancelled = true
    }
  }, [])

  // Anything the account has not themed comes back null, so fall back per colour rather than
  // per response — a theme with only OnSite set still gets sensible values for the rest.
  useEffect(() => {
    let cancelled = false

    ProjectGuestPortalClient.GetTheme().then((response) => {
      if (cancelled || !response.successful) return

      const data = response.data || {}
      setTheme({
        onSite: data.onSite || DEFAULT_GUEST_THEME.onSite,
        home: data.home || DEFAULT_GUEST_THEME.home,
        notWorking: data.notWorking || DEFAULT_GUEST_THEME.notWorking,
        noResponse: data.noResponse || DEFAULT_GUEST_THEME.noResponse,
      })
    })

    return () => {
      cancelled = true
    }
  }, [])

  // What the booking button/modal call things instead of the generic word "asset". Not
  // finding one (no asset type is flagged system-default) just leaves the generic word in
  // place wherever this is read.
  useEffect(() => {
    let cancelled = false

    ProjectGuestPortalClient.GetAssetTypeName().then((response) => {
      if (cancelled || !response.successful) return
      setAssetTypeName(response.data || undefined)
    })

    return () => {
      cancelled = true
    }
  }, [])

  const selectedProject = projects.find((p: any) => p.id === selectedProjectId)

  return (
    <ProjectGuestContext.Provider
      value={{
        projects,
        selectedProjectId,
        selectedProject,
        setSelectedProjectId,
        loading,
        reload: load,
        profile,
        theme,
        assetTypeName,
      }}
    >
      {children}
    </ProjectGuestContext.Provider>
  )
}

export function useProjectGuest() {
  return useContext(ProjectGuestContext)
}
